/**
 * CandidateTable — sortable, filterable candidate data grid.
 *
 * Spec props:
 *   candidates        array   filtered list to display
 *   loading           boolean show skeleton when true
 *   onCandidateClick  (candidate) => void  row/name click handler
 *   selectedIds       Set     set of selected candidate_ids
 *   onSelectCandidate (id) => void
 *   onSelectAll       () => void
 *
 * Extended props (passed from CandidatesPage):
 *   allSelected, sortField, sortDir, onSort,
 *   colFilters, onColFilter, uniqueSources, uniqueStatuses,
 *   showScorePopover, onSetScorePopover,
 *   onViewProfile, onViewScore, onSetV4Modal,
 *   onScoreCandidate, scoringId,
 *   onScreen, onSchedule, onDeleteCandidate, onUpdateCandidates,
 *   hasActiveFilters, onClearFilters,
 *   onAddCandidate, onSourceAI, sourcingLoading,
 *   allCandidatesCount
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronUp, ChevronDown, Filter, Loader2, X, Search,
  Phone, Mail, MapPin, Briefcase, ExternalLink, Linkedin,
  FileText, User, Zap, Play, Calendar, Trash2,
  BarChart3, CheckCircle, AlertCircle, Sparkles,
  SlidersHorizontal,
} from 'lucide-react';
import ScoreCard from '../ScoreCard';
import { ScoreBadge } from '../ScoreBadge';
import EnrichContactBadge from '../EnrichContactBadge';
import V4ScoreReportModal from './V4ScoreReportModal';
import api from '@/lib/api';
import { useToast } from '../../contexts/ToastContext';

// ─── Local constants (mirror CandidatesPage values) ──────────────────────────

const STATUS_CONFIG = {
  pending:     { bg: 'bg-slate-100',   text: 'text-slate-700',   dot: 'bg-slate-400',   label: 'Pending' },
  scored:      { bg: 'bg-amber-50',    text: 'text-amber-700',   dot: 'bg-amber-500',   label: 'Scored' },
  shortlisted: { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Shortlisted' },
  screened:    { bg: 'bg-blue-100',    text: 'text-blue-700',    dot: 'bg-blue-500',    label: 'Screened' },
  rejected:    { bg: 'bg-red-100',     text: 'text-red-700',     dot: 'bg-red-500',     label: 'Rejected' },
};

const SOURCE_ICONS = {
  'Manual': '✏️', 'GitHub': '⌥', 'Stack Overflow': '◆',
  'Dev.to': '◈', 'Reddit': '●', 'Twitter/X': '𝕏',
  'LinkedIn': 'in',
};

const SOURCE_BG = {
  'Manual': 'bg-slate-100 text-slate-600',
  'GitHub': 'bg-gray-900 text-white',
  'Stack Overflow': 'bg-orange-50 text-orange-700',
  'Dev.to': 'bg-slate-800 text-white',
  'Reddit': 'bg-orange-100 text-orange-800',
  'LinkedIn': 'bg-[#0A66C2] text-white',
};
const getSourceStyle = (col) => SOURCE_BG[col] || 'bg-blue-50 text-[#2563eb]';
const scoreColor = (s) => !s && s !== 0 ? 'text-slate-300' : s >= 85 ? 'text-emerald-600' : s >= 70 ? 'text-[#2563eb]' : s >= 50 ? 'text-amber-600' : 'text-red-500';

// Defensive helper — always returns a number or null, never an object
const normalizeScore = (value) =>
  typeof value === 'object' && value !== null
    ? (typeof value.score === 'number' ? value.score : null)
    : (typeof value === 'number' ? value : null);

const GRID_COLS = '40px 1.8fr 100px 160px 110px 100px 80px 60px 65px 160px';

const getScreeningStatusMeta = (status) => {
  switch (status) {
    case "pending":
      return { label: "Scheduled", className: "bg-blue-50 text-blue-700" };
    case "in_progress":
      return { label: "In progress", className: "bg-amber-50 text-amber-700" };
    case "completed":
      return { label: "Completed", className: "bg-emerald-50 text-emerald-700" };
    case "no_answer":
      return { label: "No answer", className: "bg-rose-50 text-rose-700" };
    case "handoff":
      return { label: "Human handoff", className: "bg-violet-50 text-violet-700" };
    default:
      return { label: "Not started", className: "bg-slate-100 text-slate-600" };
  }
};


// ─── ColumnFilter (extracted from CandidatesPage) ────────────────────────────

function ColumnFilter({ column, filters, onChange, options = [], placeholder = 'Filter...' }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const hasValue = filters[column] && filters[column] !== '' && filters[column] !== 'all';

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative inline-block" onClick={e => e.stopPropagation()}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`ml-1 p-0.5 rounded transition-colors ${hasValue ? 'text-[#2563eb] bg-blue-100' : 'text-slate-300 hover:text-slate-500'}`}
        title={`Filter by ${column}`}
        data-testid={`col-filter-btn-${column}`}
      >
        <Filter className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 z-50 bg-white border border-slate-200 rounded-xl shadow-xl p-3 min-w-[180px]">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-2 font-semibold">Filter: {column}</p>

          {options.length > 0 ? (
            <div className="space-y-1">
              <button
                onClick={() => { onChange(column, 'all'); setOpen(false); }}
                className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition-colors ${filters[column] === 'all' || !filters[column] ? 'bg-[#2563eb] text-white' : 'hover:bg-slate-50 text-slate-600'}`}
              >All</button>
              {options.map(opt => (
                <button key={opt.value}
                  onClick={() => { onChange(column, opt.value); setOpen(false); }}
                  className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition-colors ${filters[column] === opt.value ? 'bg-[#2563eb] text-white' : 'hover:bg-slate-50 text-slate-600'}`}
                >{opt.label}</button>
              ))}
            </div>
          ) : column === 'score' || column === 'relevance' ? (
            <div className="space-y-1">
              <p className="text-[10px] text-slate-400 mb-1">Score range</p>
              {[
                { v: 'all',    l: 'All Scores' },
                { v: 'high',   l: '🟢 High (80+)' },
                { v: 'medium', l: '🟡 Medium (60-79)' },
                { v: 'low',    l: '🔴 Low (< 60)' },
                { v: '0',      l: '○ Unscored only' },
              ].map(opt => (
                <button key={opt.v}
                  onClick={() => { onChange(column, opt.v); setOpen(false); }}
                  className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition-colors ${(filters[column] || 'all') === opt.v ? 'bg-[#2563eb] text-white' : 'hover:bg-slate-50 text-slate-600'}`}
                >{opt.l}</button>
              ))}
            </div>
          ) : (
            <div>
              <input
                autoFocus
                type="text"
                value={filters[column] || ''}
                onChange={e => onChange(column, e.target.value)}
                placeholder={placeholder}
                className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-700 placeholder-slate-400 focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb]/30 outline-none"
              />
              {filters[column] && (
                <button onClick={() => { onChange(column, ''); setOpen(false); }} className="mt-1.5 text-[10px] text-slate-400 hover:text-red-500 w-full text-center">
                  Clear filter
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ─── ColHeader (extracted from CandidatesPage) ───────────────────────────────

function ColHeader({ label, field, sortField, sortDir, onSort, filterKey, filters, onFilter, filterOptions, filterPlaceholder }) {
  const isActive = sortField === field;
  const hasFilter = filters[filterKey] && filters[filterKey] !== 'all' && filters[filterKey] !== '';
  return (
    <div className="flex items-center gap-0.5 group">
      <button
        onClick={() => field && onSort(field)}
        className={`flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider transition-colors ${field ? 'cursor-pointer' : 'cursor-default'} ${isActive ? 'text-[#2563eb]' : 'text-slate-500 hover:text-slate-700'}`}
      >
        {label}
        {field && (isActive
          ? (sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-[#2563eb]" /> : <ChevronDown className="w-3 h-3 text-[#2563eb]" />)
          : <ChevronUp className="w-3 h-3 text-slate-300 group-hover:text-slate-400" />
        )}
      </button>
      {filterKey && (
        <ColumnFilter
          column={filterKey}
          filters={filters}
          onChange={onFilter}
          options={filterOptions}
          placeholder={filterPlaceholder}
        />
      )}
      {hasFilter && (
        <span className="w-1.5 h-1.5 rounded-full bg-[#2563eb] flex-shrink-0" title="Filter active" />
      )}
    </div>
  );
}


// ─── Loading skeleton ─────────────────────────────────────────────────────────

function TableSkeleton() {
  return (
    <div className="flex flex-col h-full bg-slate-50 p-8" style={{ fontFamily: 'Georgia, serif' }}>
      <div className="h-8 bg-slate-100 rounded-lg w-48 mb-6 animate-pulse" />
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200"
          style={{ gridTemplateColumns: GRID_COLS }}>
          {[...Array(10)].map((_, i) => <div key={i} className="h-3 bg-slate-200 rounded animate-pulse" />)}
        </div>
        {[...Array(5)].map((_, i) => (
          <div key={i} className="grid gap-2 px-4 py-4 border-b border-slate-100 items-center"
            style={{ gridTemplateColumns: GRID_COLS }}>
            <div className="w-4 h-4 bg-slate-100 rounded animate-pulse" />
            <div className="space-y-1.5">
              <div className="h-3 bg-slate-200 rounded w-32 animate-pulse" />
              <div className="h-2.5 bg-slate-100 rounded w-24 animate-pulse" />
            </div>
            {[...Array(8)].map((_, j) => (
              <div key={j} className="h-3 bg-slate-100 rounded animate-pulse"
                style={{ width: `${60 + (j * 7) % 40}%` }} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}


// ─── Main export ──────────────────────────────────────────────────────────────

export function CandidateTable({
  // spec props
  candidates,
  loading,
  onCandidateClick,
  selectedIds,
  onSelectCandidate,
  onSelectAll,
  // extended props
  allSelected,
  sortField,
  sortDir,
  onSort,
  colFilters,
  onColFilter,
  uniqueSources,
  uniqueStatuses,
  showScorePopover,
  onSetScorePopover,
  onViewProfile,
  onViewScore,
  onSetV4Modal,
  onScoreCandidate,
  scoringId,
  onScreen,
  onSchedule,
  onDeleteCandidate,
  onUpdateCandidates,
  hasActiveFilters,
  onClearFilters,
  onAddCandidate,
  onSourceAI,
  sourcingLoading,
  allCandidatesCount,
  onRefreshCandidates,
}) {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [callModal, setCallModal] = useState(null);
  const [callPhone, setCallPhone] = useState("");
  const [callLoading, setCallLoading] = useState(false);
  const [screeningStatusById, setScreeningStatusById] = useState({});
  const [voiceSummaryById, setVoiceSummaryById] = useState({});
  const [scoreModalOpen,    setScoreModalOpen]    = useState(false);
  const [scoreModalSource,  setScoreModalSource]  = useState(null);   // "linkedin" | "resume"
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  const openScoreModal = (c, source, e) => {
    e?.stopPropagation();
    setSelectedCandidate(c);
    setScoreModalSource(source);
    setScoreModalOpen(true);
  };

  const closeScoreModal = () => {
    setScoreModalOpen(false);
    setSelectedCandidate(null);
    setScoreModalSource(null);
  };

  const handleCandidateClick = (c) => {
    if (onCandidateClick) onCandidateClick(c);
    else navigate(`/candidates/${c.candidate_id}`);
  };

  const handleTriggerCall = async () => {
    setCallLoading(true);
    try {
      await api.post("/voice/trigger-call", {
        candidate_id: callModal.candidateId,
        job_id: callModal.jobId,
        workspace_id: callModal.workspaceId,
        phone_override: callPhone || undefined,
      });
      addToast(`AI Screening call triggered for ${callModal.name}`, "success");
      setCallModal(null);
      setCallPhone("");
    } catch (err) {
      addToast(
        err?.response?.data?.detail || "Failed to trigger call",
        "error"
      );
    } finally {
      setCallLoading(false);
    }
  };

  const handleScoreResume = async (e, cid) => {
    e.stopPropagation();
    try {
      await api.post(`/candidates/${cid}/score-resume`);
      if (onRefreshCandidates) {
        await onRefreshCandidates();
      }
      addToast('Resume scored!', 'success');
    } catch (err) {
      addToast(err?.response?.data?.detail || 'Scoring failed — upload a resume first.', 'error');
    }
  };

  useEffect(() => {
    if (!candidates || candidates.length === 0) return;

    let isCancelled = false;

    const fetchStatuses = async () => {
      try {
        const results = await Promise.all(
          candidates.map(async (c) => {
            if (!c.candidate_id) return null;
            try {
              const res = await api.get(
                `/voice/screening-status/${c.candidate_id}`
              );
              return { candidateId: c.candidate_id, status: res.data.status };
            } catch {
              return { candidateId: c.candidate_id, status: "not_started" };
            }
          })
        );

        if (isCancelled) return;

        const next = {};
        results.forEach((r) => {
          if (r) next[r.candidateId] = r.status;
        });
        setScreeningStatusById(next);
        return next;
      } catch {
        return null;
      }
    };

    const TERMINAL = new Set([
      "completed", "no_answer", "handoff", "not_started",
    ]);

    let intervalId = null;

    const startPolling = async () => {
      const initial = await fetchStatuses();

      if (isCancelled) return;

      // Check if any active status exists — if so, start 10s polling
      const hasActive = initial &&
        Object.values(initial).some((s) => !TERMINAL.has(s));

      if (hasActive) {
        intervalId = setInterval(async () => {
          const updated = await fetchStatuses();
          if (isCancelled && intervalId) {
            clearInterval(intervalId);
            return;
          }
          // Stop polling once all statuses are terminal
          const stillActive = updated &&
            Object.values(updated).some((s) => !TERMINAL.has(s));
          if (!stillActive && intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
        }, 10000);
      }
    };

    startPolling();

    return () => {
      isCancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [candidates]);

  useEffect(() => {
    if (!candidates || candidates.length === 0) return;

    let isCancelled = false;

    const fetchVoiceSummary = async () => {
      try {
        const results = await Promise.all(
          candidates.map(async (c) => {
            if (!c.candidate_id) return null;
            try {
              const res = await api.get(
                `/voice/summary-status/${c.candidate_id}`
              );
              return {
                candidateId: c.candidate_id,
                overallScore: res.data.overall_score,
                hasIssues: res.data.has_proctoring_issues,
              };
            } catch {
              return {
                candidateId: c.candidate_id,
                overallScore: null,
                hasIssues: false,
              };
            }
          })
        );

        if (isCancelled) return;

        const next = {};
        results.forEach((r) => {
          if (!r) return;
          next[r.candidateId] = {
            overallScore: r.overallScore,
            hasIssues: r.hasIssues,
          };
        });
        setVoiceSummaryById(next);
      } catch {
        // fail-soft
      }
    };

    fetchVoiceSummary();

    return () => { isCancelled = true; };
  }, [candidates]);

  if (loading) return <TableSkeleton />;

  // (comment placeholder removed)

  // All candidates are visible — the v4 gate already pre-filters for relevance
  const visibleCandidates = candidates;

  return (
    <>
    <div className="flex-1 overflow-auto px-8 py-5">
      {visibleCandidates.length === 0 ? (
        /* ── Empty states ───────────────────────────────────────── */
        <div className="text-center py-20 bg-white border border-slate-200 rounded-2xl">
          {allCandidatesCount === 0 ? (
            <>
              <Sparkles className="w-14 h-14 text-[#2563eb]/25 mx-auto mb-4" />
              <h3 className="text-slate-700 font-semibold mb-2">No candidates yet</h3>
              <p className="text-slate-400 text-sm mb-6">Add manually or source from GitHub + Exa AI Neural Search.</p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={onSourceAI}
                  disabled={sourcingLoading}
                  className="bg-[#2563eb] text-white px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 hover:bg-[#1d4ed8]"
                >
                  <Sparkles className="w-4 h-4" /> Source with AI
                </button>
                <button
                  onClick={onAddCandidate}
                  className="bg-white border border-slate-200 text-slate-700 px-5 py-2.5 rounded-xl text-sm"
                >
                  Add Manually
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                <Search className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-1">No candidates found</h3>
              <p className="text-slate-500 text-sm mb-5">Try adjusting your filters or search term.</p>
              <button
                onClick={onClearFilters}
                className="flex items-center gap-2 px-4 py-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-sm font-semibold rounded-xl transition-colors"
                data-testid="clear-filters-btn"
              >
                <X className="w-3.5 h-3.5" /> Clear all filters
              </button>
            </div>
          )}
        </div>
      ) : (
        /* ── Table ──────────────────────────────────────────────── */
        <div className="bg-white/85 backdrop-blur-sm border border-slate-200/60 rounded-2xl shadow-lg overflow-hidden">
          {/* Table header with per-column filters */}
          <div className="grid gap-2 px-4 py-3 sticky top-0 z-20 bg-slate-50/90 backdrop-blur-md shadow-sm border-b border-slate-200/70"
            style={{ gridTemplateColumns: GRID_COLS }}>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onSelectAll}
                className="w-4 h-4 rounded border-slate-300 accent-[#2563eb] cursor-pointer"
                data-testid="select-all-checkbox"
              />
            </div>

            <ColHeader label="Candidate" field="name" sortField={sortField} sortDir={sortDir} onSort={onSort}
              filterKey="name" filters={colFilters} onFilter={onColFilter} filterPlaceholder="Filter by name/email..." />

            <ColHeader label="Source" field="source" sortField={sortField} sortDir={sortDir} onSort={onSort}
              filterKey="source" filters={colFilters} onFilter={onColFilter}
              filterOptions={[...uniqueSources]} />

            <div className="flex items-center gap-0.5">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Contact / Location</span>
              <ColumnFilter column="location" filters={colFilters} onChange={onColFilter} placeholder="Filter by city/state..." />
              {colFilters.location && <span className="w-1.5 h-1.5 rounded-full bg-[#2563eb]" />}
            </div>

            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Profile</div>

            <ColHeader label="AI Score" field="score" sortField={sortField} sortDir={sortDir} onSort={onSort}
              filterKey="score" filters={colFilters} onFilter={onColFilter} />

            <ColHeader label="Relevance" field="relevance" sortField={sortField} sortDir={sortDir} onSort={onSort}
              filterKey="relevance" filters={colFilters} onFilter={onColFilter} />

            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Voice</div>

            <ColHeader label="Status" field="status" sortField={sortField} sortDir={sortDir} onSort={onSort}
              filterKey="status" filters={colFilters} onFilter={onColFilter}
              filterOptions={uniqueStatuses} />

            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Actions</div>
          </div>

          {/* Rows */}
          <div className="divide-y divide-slate-200/60 bg-transparent">
            {visibleCandidates.map((c) => {
              const isSourced = c.source_type === 'sourced';
              const srcCol    = c.source_column || (isSourced ? 'Sourced' : 'Manual');
              const srcStyle  = getSourceStyle(srcCol);
              const srcIcon   = SOURCE_ICONS[srcCol] || '◉';
              const score     = c.score_details?.overall_score ?? (c.score != null ? Math.round(c.score * 10) : null);
              const st        = STATUS_CONFIG[c.status] || STATUS_CONFIG.pending;
              const sd        = c.sourced_data || {};
              const isSelected = selectedIds.has(c.candidate_id);

              // ── Score display values: use pre-normalized fields from CandidatesPage ──
              const getNumeric = (v) =>
                typeof v === 'number' && !Number.isNaN(v) ? v : null;

              const liObj = (c.linkedinscore && typeof c.linkedinscore === 'object' ? c.linkedinscore : null)
                || (c.linkedin_score && typeof c.linkedin_score === 'object' ? c.linkedin_score : null)
                || null;

              const liScoreFromObj =
                getNumeric(liObj?.score) ??
                getNumeric(liObj?.overall_score) ??
                getNumeric(liObj?.breakdown?.total_score);

              const liScoreLegacy =
                getNumeric(c.relevancescore) ??
                getNumeric(c.relevance_score);

              const rawLiScore =
                getNumeric(c.li_score_display) ??   // pre-normalized (preferred)
                liScoreFromObj ?? liScoreLegacy;

              // UI rule: treat 0 or missing as unscored (render '—')
              const liScore = rawLiScore != null && rawLiScore !== 0 ? rawLiScore : null;

              const rawResumeScore =
                getNumeric(c.resume_score_display) ??   // pre-normalized (preferred)
                (c.resumescore && typeof c.resumescore.score === 'number' ? c.resumescore.score : null) ??
                (c.resume_score && typeof c.resume_score.score === 'number' ? c.resume_score.score : null) ??
                null;

              const resumeScore = rawResumeScore != null && rawResumeScore >= 0 ? rawResumeScore : null;

              return (
                <div
                  key={c.candidate_id}
                  data-testid={`candidate-row-${c.candidate_id}`}
                  className={`group grid gap-2 px-4 py-4 items-center transition-colors duration-200 ${isSelected ? 'bg-blue-50/60' : 'hover:bg-slate-50/80'}`}
                  style={{ gridTemplateColumns: GRID_COLS }}
                >
                  {/* Checkbox */}
                  <div className="flex items-center pt-0.5">
                    <input type="checkbox" checked={isSelected} onChange={() => onSelectCandidate(c.candidate_id)}
                      className="w-4 h-4 rounded border-slate-300 accent-[#2563eb] cursor-pointer"
                      data-testid={`select-${c.candidate_id}`} />
                  </div>

                  {/* Name */}
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-100 to-emerald-50 border border-white shadow-sm flex items-center justify-center text-sm font-bold text-blue-700 flex-shrink-0">
                        {c.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p
                            onClick={() => handleCandidateClick(c)}
                            className="text-sm font-bold text-slate-900 truncate hover:text-[#2563eb] cursor-pointer transition-colors"
                          >{c.name}</p>
                          {c.source_query && (
                            <span className="inline-block text-[10px] bg-slate-100 text-slate-400 rounded px-1.5 py-0.5 mt-0.5 truncate max-w-[180px]" title={c.source_query}>
                              🔍 {c.source_query}
                            </span>
                          )}
                          {(c.openToWork || c.open_to_work) && (
                            <span
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-300 flex-shrink-0"
                              title={c.otwSignalsFound?.join(', ') || 'Open to Work'}
                              data-testid={`otw-badge-${c.candidate_id}`}
                            >
                              🟢 Open to Work
                              {typeof c.otwConfidence === 'number' && c.otwConfidence > 0 && (
                                <span className="text-[8px] text-emerald-600 ml-0.5">({c.otwConfidence})</span>
                              )}
                            </span>
                          )}
                        </div>
                        {isSourced && sd.current_title   && <p className="text-xs text-slate-500 truncate mt-0.5">{sd.current_title}</p>}
                        {isSourced && sd.current_company && <p className="text-xs text-slate-400 truncate">{sd.current_company}</p>}
                        {!isSourced && c.phone           && <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" />{c.phone}</p>}
                      </div>
                    </div>
                  </div>

                  {/* Source badge + enrichment */}
                  <div className="space-y-1">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold ${srcStyle}`}
                      data-testid={`source-badge-${c.candidate_id}`}>
                      <span>{srcIcon}</span>
                      <span className="truncate">{srcCol}</span>
                    </span>
                    {c.source_column === 'LinkedIn' && (
                      <div>
                        {c.enrichment_status === 'enriched' && (
                          <span className="flex items-center gap-0.5 text-[9px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">
                            <CheckCircle className="w-2.5 h-2.5" /> Enriched
                          </span>
                        )}
                        {c.enrichment_status === 'enriching' && (
                          <span className="flex items-center gap-0.5 text-[9px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">
                            <Loader2 className="w-2.5 h-2.5 animate-spin" /> Enriching
                          </span>
                        )}
                        {c.enrichment_status === 'rejected_non_us' && (
                          <span className="flex items-center gap-0.5 text-[9px] font-semibold text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full">
                            <AlertCircle className="w-2.5 h-2.5" /> Non-US
                          </span>
                        )}
                        {!c.enrichment_status && (
                          <span className="text-[9px] text-slate-300">Not enriched</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Contact / Location */}
                  <div className="min-w-0 text-xs">
                    {isSourced ? (
                      <>
                        {sd.us_location && (
                          <p className="flex items-center gap-1 text-slate-500 truncate">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{sd.us_location.replace('Inferred from company: ', '').replace('Inferred from bio: ', '')}</span>
                            {c.location_verified  && <span className="text-emerald-500" title="Location verified">✓</span>}
                            {!c.location_verified && c.us_location && <span className="text-amber-400" title="Location unverified">?</span>}
                          </p>
                        )}
                        {c.captured_email
                          ? <p className="flex items-center gap-1 text-emerald-600 truncate mt-0.5"><Mail className="w-3 h-3 flex-shrink-0" />{c.captured_email}</p>
                          : <p className="text-slate-300 italic text-[11px]">Email not public</p>
                        }
                        {c.captured_phone && <p className="flex items-center gap-1 text-emerald-600 mt-0.5"><Phone className="w-3 h-3 flex-shrink-0" />{c.captured_phone}</p>}
                        {sd.experience_years != null && <p className="flex items-center gap-1 text-slate-400 mt-0.5"><Briefcase className="w-3 h-3 flex-shrink-0" />{sd.experience_years}y</p>}
                      </>
                    ) : (
                      <>
                        <p className="flex items-center gap-1 text-slate-600 truncate"><Mail className="w-3 h-3 flex-shrink-0" />{c.email}</p>
                        {c.phone && <p className="flex items-center gap-1 text-slate-400 mt-0.5"><Phone className="w-3 h-3 flex-shrink-0" />{c.phone}</p>}
                      </>
                    )}
                  </div>

                  {/* Profile */}
                  <div className="min-w-0 text-xs">
                    {isSourced && c.source_profile_url ? (
                      <>
                        <a href={c.source_profile_url} target="_blank" rel="noopener noreferrer"
                          className={`inline-flex items-center gap-1 font-medium hover:opacity-80 transition-opacity ${srcCol === 'LinkedIn' ? 'text-[#0A66C2]' : 'text-[#2563eb]'}`}
                          data-testid={`profile-url-${c.candidate_id}`} title={c.source_profile_url}>
                          {srcCol === 'LinkedIn'
                            ? <Linkedin className="w-4 h-4 flex-shrink-0" />
                            : (
                              <>
                                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate text-[11px]">
                                  {c.source_profile_url.replace('https://', '').split('/').slice(0, 3).join('/')}
                                </span>
                              </>
                            )
                          }
                        </a>
                        {sd.skills?.length > 0 && (
                          <div className="flex flex-wrap gap-0.5 mt-1">
                            {sd.skills.slice(0, 2).map((s) => (
                              <span key={s} className={`px-1.5 py-0.5 text-[9px] rounded-full border ${srcCol === 'LinkedIn' ? 'bg-[#0A66C2]/10 text-[#0A66C2] border-[#0A66C2]/20' : 'bg-blue-50 text-[#2563eb] border-blue-100'}`}>{s}</span>
                            ))}
                            {sd.skills.length > 2 && <span className="text-[9px] text-slate-400">+{sd.skills.length - 2}</span>}
                          </div>
                        )}
                      </>
                    ) : (
                      <button onClick={() => onViewProfile(c)} className="flex items-center gap-1 text-slate-500 hover:text-[#2563eb] hover:underline">
                        <FileText className="w-3 h-3" /> Resume
                      </button>
                    )}
                  </div>

                  {/* AI Score — Resume score only + detail icon */}
                  <div className="relative space-y-1" data-testid={`score-cell-${c.candidate_id}`}>
                    {resumeScore != null ? (
                      <div className="flex items-center gap-1.5">
                        <ScoreBadge
                          score={resumeScore}
                          source="resume"
                          onOpenReport={() => onSetV4Modal({ candidate: c, source: 'resume' })}
                        />
                        <button
                          type="button"
                          onClick={(e) => openScoreModal(c, 'resume', e)}
                          className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-slate-200 bg-white hover:bg-emerald-50 hover:border-emerald-300 text-slate-400 hover:text-emerald-700 transition-colors flex-shrink-0"
                          title="View resume scoring details"
                          data-testid={`resume-score-detail-btn-${c.candidate_id}`}
                        >
                          <BarChart3 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : <span className="text-slate-300 text-sm">—</span>}
                  </div>

                  {/* Relevance — LinkedIn score only */}
                  <div className="relative flex items-center gap-1">
                    <ScoreBadge
                      score={liScore}
                      source="linkedin"
                      onOpenReport={liScore != null ? () => onSetV4Modal({ candidate: c, source: 'linkedin' }) : null}
                    />
                    {(() => {
                      const rs = typeof c?.resumescore === 'object'
                        ? c?.resumescore?.score ?? null
                        : c?.resumescore ?? null;
                      return rs != null ? (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ml-1 ${
                          rs >= 80 ? 'bg-emerald-100 text-emerald-700' :
                          rs >= 60 ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>CV {rs}</span>
                      ) : null;
                    })()}
                  </div>

                  {/* Voice Score */}
                  <div>
                    {c.voice_screening_score?.overall_score != null ? (
                      <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-bold border ${
                        c.voice_screening_score.overall_score >= 80 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        c.voice_screening_score.overall_score >= 60 ? 'bg-blue-50 text-[#2563eb] border-blue-200' :
                                                                       'bg-red-50 text-red-600 border-red-200'
                      }`} data-testid={`voice-score-${c.candidate_id}`}>
                        {c.voice_screening_score.overall_score}
                      </span>
                    ) : <span className="text-slate-300 text-sm">—</span>}
                  </div>

                  {/* Status */}
                  <div className="flex flex-col items-start gap-0.5">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide ${st.bg} ${st.text}`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />{st.label}
                    </span>
                    {c.email_sent && <p className="text-[9px] text-emerald-600 flex items-center gap-0.5"><Mail className="w-2.5 h-2.5" />Notified</p>}
                    {(() => {
                      const scrStatus = screeningStatusById[c.candidate_id];
                      if (!scrStatus) return null;
                      const { label: screeningLabel, className: screeningClass } = getScreeningStatusMeta(scrStatus);
                      return (
                        <span
                          className={"inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold " + screeningClass}
                          data-testid={`screening-status-${c.candidate_id}`}
                        >
                          {screeningLabel}
                        </span>
                      );
                    })()}
                    {(() => {
                      const voiceSummary = voiceSummaryById[c.candidate_id];
                      const voiceScore =
                        voiceSummary && typeof voiceSummary.overallScore === "number"
                          ? voiceSummary.overallScore
                          : null;
                      const voiceHasIssues = voiceSummary?.hasIssues;
                      if (voiceScore === null) return null;
                      return (
                        <div className="flex items-center gap-1" data-testid={`voice-summary-${c.candidate_id}`}>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-slate-50 text-slate-700">
                            Voice: {voiceScore}
                          </span>
                          {voiceHasIssues && (
                            <span
                              className="inline-flex h-5 w-5 items-center justify-center rounded-full
                                         bg-rose-50 text-rose-700 text-[10px] font-bold"
                              title="Proctoring issues on last AI call"
                              data-testid={`voice-proctoring-warning-${c.candidate_id}`}
                            >
                              !
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Phase 8 — SalesQL enrichment badge */}
                  <div onClick={e => e.stopPropagation()}>
                    <EnrichContactBadge
                      candidateId={c.candidate_id}
                      enrichment={c.enrichment || null}
                      onEnriched={(data) => {
                        onUpdateCandidates(prev =>
                          prev.map(cx =>
                            cx.candidate_id === c.candidate_id ? { ...cx, enrichment: data } : cx
                          )
                        );
                      }}
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    <button
                      data-testid={`trigger-call-${c.candidate_id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCallPhone(c.phone || "");
                        setCallModal({
                          candidateId: c.candidate_id,
                          jobId: c.job_id,
                          workspaceId: c.workspace_id,
                          name: c.name || "Candidate",
                          phone: c.phone || "",
                        });
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl
                                 bg-purple-50 text-purple-700 hover:bg-purple-100
                                 font-semibold transition-colors"
                      title="Trigger AI Screening Call"
                    >
                      <Phone className="w-4 h-4" />
                    </button>
                    <button data-testid={`view-profile-${c.candidate_id}`} onClick={() => onViewProfile(c)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-50 text-slate-600 hover:bg-slate-100 font-semibold transition-colors" title="View">
                      {isSourced ? <User className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
                    </button>
                    {score != null && (
                      <button data-testid={`view-score-${c.candidate_id}`} onClick={() => onViewScore(c)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-50 text-slate-600 hover:bg-slate-100 font-semibold transition-colors" title="Score Report">
                        <BarChart3 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {c.status === 'pending' && c.source_type !== 'sourced' && (
                      <button data-testid={`score-candidate-${c.candidate_id}`}
                        onClick={() => onScoreCandidate(c.candidate_id)} disabled={scoringId === c.candidate_id}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-blue-50 text-blue-700 hover:bg-blue-100 font-semibold transition-colors disabled:opacity-50">
                        {scoringId === c.candidate_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                      </button>
                    )}
                    {c.status === 'shortlisted' && (
                      <button data-testid={`start-screening-${c.candidate_id}`} onClick={() => onScreen(c)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-blue-50 text-blue-700 hover:bg-blue-100 font-semibold transition-colors">
                        <Play className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {(c.status === 'shortlisted' || c.status === 'screened') && (
                      <button data-testid={`schedule-interview-${c.candidate_id}`}
                        onClick={() => onSchedule(c)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-blue-50 text-blue-700 hover:bg-blue-100 font-semibold transition-colors"
                        title="Schedule an interview">
                        <Calendar className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {c?.resume_uploaded_at && (
                      <button
                        onClick={(e) => handleScoreResume(e, c.candidate_id)}
                        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl font-semibold transition-all ${
                          (() => {
                            const rs = typeof c?.resumescore === 'object'
                              ? c?.resumescore?.score ?? null
                              : (typeof c?.resumescore === 'number' ? c?.resumescore : null);
                            return rs
                              ? 'text-slate-600 bg-slate-50 hover:text-emerald-600 hover:bg-emerald-50'
                              : 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100';
                          })()
                        }`}
                        title={c?.resumescore ? 'Re-score Resume' : 'Score Resume'}
                        data-testid={`score-resume-${c.candidate_id}`}
                      >
                        <FileText className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button data-testid={`delete-candidate-${c.candidate_id}`}
                      onClick={() => onDeleteCandidate(c.candidate_id)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 font-semibold transition-colors" title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Table footer */}
          <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
            <p className="text-xs text-slate-400">
              Showing <strong>{visibleCandidates.length}</strong> of {allCandidatesCount} candidates
              {selectedIds.size > 0 && <span className="ml-2 text-[#2563eb]">· {selectedIds.size} selected</span>}
            </p>
            {hasActiveFilters && (
              <button onClick={onClearFilters} className="text-xs text-[#2563eb] hover:underline flex items-center gap-1">
                <X className="w-3 h-3" /> Clear filters
              </button>
            )}
          </div>
        </div>
      )}

      {callModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50
                        flex items-center justify-center p-4
                        animate-in fade-in duration-200">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl
                          border border-slate-200/60 w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-1">
              Trigger AI Screening Call
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              The AI will call{" "}
              <span className="font-semibold text-slate-700">
                {callModal.name}
              </span>{" "}
              immediately and conduct a structured screening interview.
            </p>

            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={callPhone}
              onChange={(e) => setCallPhone(e.target.value)}
              placeholder="+1 (555) 000-0000"
              className="w-full px-4 py-3 rounded-xl border border-slate-200/70
                         bg-slate-50/50 focus:ring-2 focus:ring-purple-500
                         outline-none text-slate-900 transition-all mb-4"
              data-testid="call-modal-phone-input"
            />

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setCallModal(null); setCallPhone(""); }}
                className="px-4 py-2 rounded-xl bg-slate-100 text-slate-600
                           hover:bg-slate-200 font-semibold transition-colors"
                data-testid="call-modal-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleTriggerCall}
                disabled={callLoading || (!callPhone && !callModal.phone)}
                className="px-5 py-2 rounded-xl bg-gradient-to-r from-violet-600
                           to-purple-600 text-white font-semibold shadow-md
                           hover:shadow-lg transition-all disabled:opacity-50
                           disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="call-modal-confirm-btn"
              >
                {callLoading ? "Triggering..." : "Start AI Call"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

      {/* Score detail modal — LinkedIn or Resume */}
      {scoreModalOpen && selectedCandidate && (
        <V4ScoreReportModal
          candidate={selectedCandidate}
          source={scoreModalSource}
          onClose={closeScoreModal}
        />
      )}
    </>
  );
}
